import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Crown, Mail, Mic, MessageCircle } from 'lucide-react';
import { Dialog, DialogSheetContent, DialogSheetHeader, DialogSheetBody, DialogDescription } from './ui/dialog';
import * as api from '../lib/api';
import { usePaywall } from '../context/PaywallContext';
import { friendlyError } from '../lib/errorReporting';

const PERKS = [
  { icon: Mail, label: 'Detección automática de gastos por Gmail' },
  { icon: Mic, label: 'Registro de gastos por voz' },
  { icon: MessageCircle, label: 'Coach financiero por chat' },
];

export default function PaywallModal() {
  const { reason, mustAcknowledge, busy, closePaywall, acknowledge, upgrade } = usePaywall();
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    if (!reason) return;
    api.getBillingStatus().then(setBilling).catch(() => {});
  }, [reason]);

  const handleUpgrade = async () => {
    try {
      if (mustAcknowledge) await api.acknowledgeTrialUrgency('upgrade').catch(() => {});
      await upgrade();
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo iniciar el pago'));
    }
  };

  // Leaving via the referral link is still a deliberate response to the
  // popup (choosing an alternative to paying), not an accidental dismissal
  // — counts as acknowledged too. Fire-and-forget so the navigation isn't delayed.
  const handleReferralLinkClick = () => {
    if (mustAcknowledge) api.acknowledgeTrialUrgency('referral').catch(() => {});
  };

  return (
    <Dialog open={!!reason} onOpenChange={(open) => { if (!open && !mustAcknowledge) closePaywall(); }}>
      <DialogSheetContent
        compact
        data-testid="paywall-modal"
        hideClose={mustAcknowledge}
        onEscapeKeyDown={(e) => { if (mustAcknowledge) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (mustAcknowledge) e.preventDefault(); }}
        onInteractOutside={(e) => { if (mustAcknowledge) e.preventDefault(); }}
      >
        <DialogSheetHeader
          title="MiPisto Pro"
          onCancel={mustAcknowledge ? undefined : closePaywall}
          cancelLabel="Ahora no"
          cancelTestId="paywall-dismiss-btn"
        />

        <DialogSheetBody className="space-y-5">
          <div>
            <div className="w-12 h-12 rounded-2xl bg-violet-500/20 flex items-center justify-center mb-3">
              <Crown className="w-6 h-6 text-violet-400" />
            </div>
            <h2 className="text-white text-lg font-semibold mb-1">Esto es parte de MiPisto Pro</h2>
            <DialogDescription className="text-slate-400">{reason}</DialogDescription>
          </div>

          <div className="space-y-2">
            {PERKS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5 text-sm text-slate-300">
                <Icon className="w-4 h-4 text-violet-400 shrink-0" />
                {label}
              </div>
            ))}
          </div>

          <button
            onClick={handleUpgrade}
            disabled={busy || !billing || !billing.billing_enabled}
            data-testid="paywall-upgrade-btn"
            className="w-full flex items-center justify-center gap-2 bg-violet-500 hover:bg-violet-600 text-on-brand px-5 py-3 rounded-xl font-medium transition-colors shadow-lg shadow-violet-500/20 disabled:opacity-50"
          >
            {busy ? 'Redirigiendo…' : billing ? `Actualizar a Pro — $${billing.price}/mes` : 'Actualizar a Pro'}
          </button>
          {billing && !billing.billing_enabled && (
            <p className="text-xs text-slate-500 text-center">Los pagos aún no están configurados en el servidor.</p>
          )}
          <a
            href="/?page=settings"
            onClick={handleReferralLinkClick}
            className="text-xs text-slate-500 hover:text-violet-300 text-center underline decoration-dotted block"
          >
            ¿No podés pagar ahora? Invitá a un amigo y seguí gratis
          </a>
          {mustAcknowledge && (
            <button onClick={() => acknowledge('dismiss')} className="w-full text-xs text-slate-500 hover:text-slate-300 text-center underline">
              Entiendo, seguir sin Pro
            </button>
          )}
        </DialogSheetBody>
      </DialogSheetContent>
    </Dialog>
  );
}
